import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ChevronRight } from 'lucide-react';

export default function ExampleReportSection() {
  const navigate = useNavigate();
  
  const handleViewReport = () => {
    navigate('/example-report');
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section id="example-report-section" className="py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Top border with gradient effect */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/4 w-1/2 h-1/2 bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute -bottom-1/2 -right-1/4 w-1/2 h-1/2 bg-secondary/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/4 right-1/4 w-1/3 h-1/3 bg-[#f9c55f]/10 rounded-full blur-[100px]" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex flex-col lg:flex-row items-center justify-between gap-12"
        >
          <motion.div 
            variants={fadeIn}
            className="lg:w-1/2 text-center lg:text-left"
          >
            <div className="inline-block relative mb-6">
              <span className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 text-primary px-6 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border border-primary/10">
                Preview Our Reports
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Experience the Power of Our <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Feedback Reports</span>
            </h2>
            
            <p className="text-xl text-gray-600 max-w-xl leading-relaxed mb-8">
              Get a firsthand look at how our comprehensive feedback analysis can transform your professional growth journey. Our reports provide actionable insights that help you identify strengths, address gaps, and chart a clear path forward.
            </p>
            
            <ul className="space-y-4 mb-8">
              {['Interactive visualizations', 'Personalized insights', 'Actionable recommendations', 'Development roadmaps'].map((item, i) => (
                <li key={i} className="flex items-center text-gray-700">
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mr-3">
                    <ChevronRight className="w-4 h-4 text-primary" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            
            <button
              onClick={handleViewReport}
              className="group relative bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto] animate-gradient text-dark px-8 py-4 rounded-xl hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-3"
            >
              <FileText className="w-5 h-5" />
              <span className="font-medium">View Example Report</span>
            </button>
          </motion.div>

          <motion.div
            variants={fadeIn}
            className="lg:w-1/2"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-xl transform rotate-3"></div>
              <img 
                src="/ReportHero.webp" 
                alt="Example Report Preview" 
                className="relative z-10 w-full h-auto rounded-xl shadow-2xl border border-white/10"
                onClick={handleViewReport}
                style={{ cursor: 'pointer' }}
              />
              
              {/* Floating badges */}
              <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-xl p-3 z-20 hidden lg:block">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900">Interactive Charts</span>
                </div>
              </div>
              
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-3 z-20 hidden lg:block">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900">Actionable Insights</span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom border with gradient effect */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </section>
  );
}